import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import { FormField, Select } from '../components/FormField';
import './Attendance.css';

export default function Attendance() {
  const { t } = useLanguage();
  const STATUS = [
    { value:'present', label:t('attendance.statusPresent'), color:'#27ae60' },
    { value:'absent', label:t('attendance.statusAbsent'), color:'#e74c3c' },
    { value:'late', label:t('attendance.statusLate'), color:'#f39c12' },
    { value:'excused', label:t('attendance.statusExcused'), color:'#3498db' },
  ];

  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [todayList, setTodayList] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [openStatus, setOpenStatus] = useState(null);

  useEffect(() => { API.get('/classes').then(r => setClasses(r.data)); }, []);

  useEffect(() => {
    if (classId) return;
    setTodayLoading(true);
    API.get('/attendance', { params: { date } })
      .then(r => setTodayList(r.data || []))
      .catch(() => setTodayList([]))
      .finally(() => setTodayLoading(false));
  }, [date, classId, saved]);

  const summary = ['absent','late','excused'].map(st => ({
    status: st,
    list: todayList.filter(a => a.status === st),
  }));

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    Promise.all([
      API.get(`/classes/${classId}/students`),
      API.get(`/attendance?class_id=${classId}&date=${date}`)
    ]).then(([s, a]) => {
      setStudents(s.data);
      const attMap = {};
      a.data.forEach(rec => { attMap[rec.student_id] = rec.status; });
      const recs = {};
      s.data.forEach(st => { recs[st.id] = attMap[st.id] || 'present'; });
      setRecords(recs);
    }).finally(() => setLoading(false));
  }, [classId, date]);

  const handleSave = async () => {
    if (!classId) return;
    setSaving(true); setSaved(false);
    try {
      const recs = students.map(s => ({ student_id: s.id, status: records[s.id] || 'present' }));
      await API.post('/attendance/bulk', { class_id: classId, date, records: recs });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setAll = (status) => {
    const r = {};
    students.forEach(s => { r[s.id] = status; });
    setRecords(r);
  };

  return (
    <div>
      <PageHeader icon="attendance" title={t('attendance.title')} subtitle={t('attendance.subtitle')} />

      <div className="att-filters">
        <FormField label={t('attendance.fieldClass')}>
          <Select value={classId} onChange={e => setClassId(e.target.value)}>
            <option value="">{t('attendance.selectClass')}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </FormField>
        <FormField label={t('attendance.fieldDate')}>
          <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </FormField>
      </div>

      {!classId && (
        <div className="att-overview">
          <div className="att-overview-title">{t('attendance.overviewTitle')}</div>
          {todayLoading ? (
            <div style={{ textAlign:'center', padding: 30, color:'#94a3b8' }}>{t('common.loading')}</div>
          ) : todayList.length === 0 ? (
            <div style={{ textAlign:'center', padding: 30, color:'#94a3b8' }}>{t('attendance.overviewEmpty')}</div>
          ) : (
            <div className="att-overview-grid">
              {summary.map(({ status, list }) => {
                const s = STATUS.find(x => x.value === status);
                const open = openStatus === status;
                return (
                  <div key={status} className="att-overview-card" style={{ '--c': s.color }}>
                    <div className="att-overview-head" onClick={() => setOpenStatus(open ? null : status)}>
                      <span className="att-overview-badge">{s.label}</span>
                      <span className="att-overview-count">{list.length}</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                        style={{ transition:'transform .2s', transform: open ? 'rotate(90deg)' : 'rotate(0)' }}>
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                    {open && (
                      list.length === 0 ? (
                        <div style={{ padding: 14, color:'#94a3b8', fontSize: '0.85rem' }}>—</div>
                      ) : (
                        <div className="att-overview-list">
                          {list.map((a, i) => (
                            <div key={a.id || i} className="att-overview-item">
                              <span className="att-overview-num">{i+1}</span>
                              <span className="att-overview-name">{a.student_name}</span>
                              <span className="att-overview-class">{a.class_name || '—'}</span>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {classId && (
        <div className="att-card">
          <div className="att-toolbar">
            <button className="att-back-btn" onClick={() => setClassId('')} title={t('common.back') || 'Back'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            </button>
            <span className="att-info">{students.length} {t('attendance.studentSuffix')}</span>
            <div className="att-bulk">
              {t('attendance.selectAll')}
              {STATUS.map(s => (
                <button key={s.value} className="btn btn-sm"
                  style={{ background: s.color+'20', color: s.color, border:`1px solid ${s.color}40` }}
                  onClick={() => setAll(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#888' }}>{t('common.loading')}</div>
          ) : students.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#aaa' }}>{t('attendance.noStudents')}</div>
          ) : (
            <div className="att-list">
              {students.map((st, i) => (
                <div key={st.id} className="att-row">
                  <div className="att-num">{i+1}</div>
                  <div className="att-name">
                    <span>{st.name}</span>
                    {st.student_number && <span className="att-stno">#{st.student_number}</span>}
                  </div>
                  <div className="att-statuses">
                    {STATUS.map(s => (
                      <button key={s.value}
                        className={`att-status-btn ${records[st.id]===s.value?'active':''}`}
                        style={{ '--status-color': s.color }}
                        onClick={() => setRecords(p => ({...p, [st.id]: s.value}))}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="att-save-row">
            {saved && <span className="att-saved">{t('attendance.saved')}</span>}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !students.length}>
              {saving ? t('common.saving') : t('attendance.btnSave')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
